import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  makeStyles,
  Button,
  IconButton,
  Box,
  Divider,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { commonInlineStyles, inputFieldStyles } from "../../styles";

const useStyles = makeStyles((theme) => ({
  formContainer: { padding: "24px" },
  formTitle: { marginBottom: theme.spacing(4), fontWeight: 500 },
  formField: {
    width: "100%",
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
  },
  button: { minWidth: 180, fontWeight: 600, fontSize: 16 },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "14px",
    marginTop: theme.spacing(0.5),
  },
  label: { marginBottom: theme.spacing(1), fontWeight: 500 },
  readOnlyValue: {
    padding: theme.spacing(1),
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    minHeight: 40,
    display: "flex",
    alignItems: "center",
  },
  addButton: {
    textTransform: "none",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
}));

const ManufacturingPlantDetailsSection = forwardRef(
  (
    {
      initialData = {},
      onChange,
      disableEdit = false,
      editMode: initialEditMode = false,
      onEditModeChange,
      onDiscard,
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
    },
    ref
  ) => {
    const classes = useStyles();
    
    // Use a ref to track what plant is currently loaded - this persists across renders
    const loadedPlantIDRef = useRef(initialData?.manufacturingPlantID || "");
    
    const [form, setForm] = useState({
      siteID: initialData.siteID || "",
      siteShortName: initialData.siteShortName || "",
      manufacturingPlantID: initialData.manufacturingPlantID || "",
      manufacturingPlantShortName: initialData.manufacturingPlantShortName || "",
      lineSuiteDetails: initialData.lineSuiteDetails || [{ lineSuiteID: "", lineSuiteShortName: "" }],
    });
    const [originalData, setOriginalData] = useState({ ...form });
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(initialEditMode);
    const [hasChanged, setHasChanged] = useState(false);



    // Load initial data - when switching plants OR when plant/site data changes
    useEffect(() => {
      const incomingPlantID = initialData?.manufacturingPlantID || "";
      const isPlantSwitch = incomingPlantID && incomingPlantID !== loadedPlantIDRef.current;
      
      // Check if any of the name fields have changed (for real-time updates)
      const hasNameChanges = (
        form.siteID !== initialData.siteID ||
        form.siteShortName !== initialData.siteShortName ||
        form.manufacturingPlantShortName !== initialData.manufacturingPlantShortName
      );
      
      // Check if lineSuiteDetails has changed
      const hasLineSuiteChanges = JSON.stringify(form.lineSuiteDetails) !== JSON.stringify(initialData.lineSuiteDetails);
      
      // Update if switching plants OR if any key fields changed
      if (isPlantSwitch || (incomingPlantID && (hasNameChanges || hasLineSuiteChanges))) {
       
        const newForm = {
          siteID: initialData.siteID || "",
          siteShortName: initialData.siteShortName || "",
          manufacturingPlantID: initialData.manufacturingPlantID || "",
          manufacturingPlantShortName: initialData.manufacturingPlantShortName || "",
          lineSuiteDetails: initialData.lineSuiteDetails || [{ lineSuiteID: "", lineSuiteShortName: "" }],
        };
        setForm(newForm);
        setOriginalData(newForm);
        loadedPlantIDRef.current = incomingPlantID;
        setErrors({});
        setHasChanged(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData?.manufacturingPlantID, initialData?.siteID, initialData?.siteShortName, initialData?.manufacturingPlantShortName, initialData?.lineSuiteDetails]);

    // Additional sync to ensure form updates when initialData changes after save
    useEffect(() => {
      // Check if we're on the same plant but data has changed (e.g., after save)
      const currentPlantID = initialData?.manufacturingPlantID || "";
      if (currentPlantID === loadedPlantIDRef.current && !hasChanged) {
        // Check if initialData differs from what we have in form
        const hasDataMismatch = (
          form.siteID !== (initialData.siteID || "") ||
          form.siteShortName !== (initialData.siteShortName || "") ||
          form.manufacturingPlantShortName !== (initialData.manufacturingPlantShortName || "") ||
          JSON.stringify(form.lineSuiteDetails) !== JSON.stringify(initialData.lineSuiteDetails || [{ lineSuiteID: "", lineSuiteShortName: "" }])
        );

        if (hasDataMismatch) {
          const newForm = {
            siteID: initialData.siteID || "",
            siteShortName: initialData.siteShortName || "",
            manufacturingPlantID: initialData.manufacturingPlantID || "",
            manufacturingPlantShortName: initialData.manufacturingPlantShortName || "",
            lineSuiteDetails: initialData.lineSuiteDetails || [{ lineSuiteID: "", lineSuiteShortName: "" }],
          };
          setForm(newForm);
          setOriginalData(newForm);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]);

    // Sync with external editMode prop - update internal state when prop changes
    useEffect(() => {
      setEditMode(initialEditMode);
    }, [initialEditMode]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      getFormData: () => form,
      getPayload: () => ({
        vendorMosymphonyID,
        brandOwnerMosymphonyID,
        updateManufacturingPlantDetailsFlag: true,
        cmoSites: [
          {
            siteDetails: {
              siteID: form.siteID,
              siteShortName: form.siteShortName,
              markForDelete: false,
            },
            manufacturingPlantDataList: [
              {
                mfgPlantDetails: {
                  mfgPlantID: form.manufacturingPlantID,
                  mfgPlantShortName: form.manufacturingPlantShortName,
                  aslNumber: null,
                  markForDelete: false,
                },
                lineDetailsList: form.lineSuiteDetails.map(line => ({
                  lineID: line.lineSuiteID,
                  lineShortName: line.lineSuiteShortName,
                })),
              },
            ],
          },
        ],
      }),
      validateForm: validate,
      hasChanges: () => hasChanged,
      resetForm: () => {
        const cleanInitialData = JSON.parse(JSON.stringify(initialData));
        const newForm = {
          siteID: cleanInitialData.siteID || "",
          siteShortName: cleanInitialData.siteShortName || "",
          manufacturingPlantID: cleanInitialData.manufacturingPlantID || "",
          manufacturingPlantShortName: cleanInitialData.manufacturingPlantShortName || "",
          lineSuiteDetails: cleanInitialData.lineSuiteDetails || [{ lineSuiteID: "", lineSuiteShortName: "" }],
        };
        setForm(newForm);
        setOriginalData(newForm);
        setErrors({});
        setHasChanged(false);
      },
    }));

    const handleChange = (index, field, value) => {
      
      const updatedLineSuiteDetails = [...form.lineSuiteDetails];
      updatedLineSuiteDetails[index] = {
        ...updatedLineSuiteDetails[index],
        [field]: value,
      };

      const updatedForm = {
        ...form,
        lineSuiteDetails: updatedLineSuiteDetails,
      };

      setForm(updatedForm);

      // Check if form has changed from original data
      const isChanged = checkForChanges(updatedForm);
      setHasChanged(isChanged);

      if (onChange) {
        onChange(updatedForm, isChanged);
      }

      // Clear error for this field when user types
      const errorKey = `lineSuite_${index}_${field}`;
      if (errors[errorKey]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      }
    };

    const handleAddLineSuite = () => {
      
      const updatedForm = {
        ...form,
        lineSuiteDetails: [
          ...form.lineSuiteDetails,
          { lineSuiteID: "", lineSuiteShortName: "" },
        ],
      };

      setForm(updatedForm);

      const isChanged = checkForChanges(updatedForm);
      setHasChanged(isChanged);

      if (onChange) {
        onChange(updatedForm, isChanged);
      }
    };

    const handleDeleteLineSuite = (index) => {
      
      if (form.lineSuiteDetails.length === 1) {
        // Don't allow deletion of the last row
        return;
      }

      const updatedForm = {
        ...form,
        lineSuiteDetails: form.lineSuiteDetails.filter((_, i) => i !== index),
      };

      setForm(updatedForm);

      const isChanged = checkForChanges(updatedForm);
      setHasChanged(isChanged);

      if (onChange) {
        onChange(updatedForm, isChanged);
      }

      // Clear errors for this row
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`lineSuite_${index}_lineSuiteID`];
        delete newErrors[`lineSuite_${index}_lineSuiteShortName`];
        return newErrors;
      });
    };

    // Function to check if form data has changed from original data
    const checkForChanges = (currentForm) => {
      // Compare lineSuiteDetails
      if (currentForm.lineSuiteDetails.length !== originalData.lineSuiteDetails.length) {
        return true;
      }

      for (let i = 0; i < currentForm.lineSuiteDetails.length; i++) {
        const current = currentForm.lineSuiteDetails[i];
        const original = originalData.lineSuiteDetails[i] || {};

        if (
          current.lineSuiteID !== (original.lineSuiteID || "") ||
          current.lineSuiteShortName !== (original.lineSuiteShortName || "")
        ) {
          return true;
        }
      }

      return false;
    };

    const validate = () => {
      let valid = true;
      let errs = {};

      // Line/Suite fields are optional - no validation needed
      // form.lineSuiteDetails.forEach((lineSuite, index) => {
      //   // No validation required
      // });

      setErrors(errs);
      return valid;
    };

    return (
      <div className="cmo-stepper-wrapper manufacturing-plant-details-step">
        <div className="stepper-container">
          <form autoComplete="off">
            <Grid className="two-col-forms-grid" container spacing={3}>
              {/* Site ID - Read Only */}
              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="siteID">
                    Site ID
                  </Typography>
                  <Typography>{form.siteID || ""}</Typography>
                </FormControl>
              </Grid>

              {/* Site Short Name - Read Only */}
              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="siteShortName">
                    Site Short Name
                  </Typography>
                  <Typography>{form.siteShortName || ""}</Typography>
                </FormControl>
              </Grid>

              {/* Manufacturing Plant ID - Read Only */}
              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="manufacturingPlantID">
                    Manufacturing Plant ID
                  </Typography>
                  <Typography>{form.manufacturingPlantID || ""}</Typography>
                </FormControl>
              </Grid>

              {/* Manufacturing Plant Short Name - Read Only */}
              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="manufacturingPlantShortName">
                    Manufacturing Plant Short Name
                  </Typography>
                  <Typography>{form.manufacturingPlantShortName || ""}</Typography>
                </FormControl>
              </Grid>

              {/* Line/Suite Details - Dynamic Rows */}
              {form.lineSuiteDetails.map((lineSuite, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={12} sm={6}>
                    <FormControl className={classes.formField}>
                      <Typography component="label" htmlFor={`lineSuiteID_${index}`}>
                        Line/Suite ID
                      </Typography>
                      {!editMode ? (
                        <Typography>{lineSuite.lineSuiteID || ""}</Typography>
                      ) : (
                        <TextField
                          id={`lineSuiteID_${index}`}
                          name={`lineSuiteID_${index}`}
                          value={lineSuite.lineSuiteID || ""}
                          onChange={(e) => handleChange(index, "lineSuiteID", e.target.value)}
                          variant="outlined"
                          placeholder="Enter text here"
                          error={!!errors[`lineSuite_${index}_lineSuiteID`]}
                          helperText={errors[`lineSuite_${index}_lineSuiteID`] || ""}
                          fullWidth
                          InputProps={{ style: commonInlineStyles.input }}
                          sx={inputFieldStyles}
                          inputProps={{
                            ...commonInlineStyles.inputProps,
                            maxLength: 50,
                          }}
                          style={commonInlineStyles.input}
                        />
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="flex-start">
                      <FormControl className={classes.formField} style={{ flex: 1 }}>
                        <Typography component="label" htmlFor={`lineSuiteShortName_${index}`}>
                          Line/Suite Short Name
                        </Typography>
                        {!editMode ? (
                          <Typography>{lineSuite.lineSuiteShortName || ""}</Typography>
                        ) : (
                          <TextField
                            id={`lineSuiteShortName_${index}`}
                            name={`lineSuiteShortName_${index}`}
                            value={lineSuite.lineSuiteShortName || ""}
                            onChange={(e) => handleChange(index, "lineSuiteShortName", e.target.value)}
                            variant="outlined"
                            placeholder="Enter text here"
                            error={!!errors[`lineSuite_${index}_lineSuiteShortName`]}
                            helperText={errors[`lineSuite_${index}_lineSuiteShortName`] || ""}
                            fullWidth
                            InputProps={{ style: commonInlineStyles.input }}
                            sx={inputFieldStyles}
                            inputProps={{
                              ...commonInlineStyles.inputProps,
                              maxLength: 50,
                            }}
                            style={commonInlineStyles.input}
                          />
                        )}
                      </FormControl>
                      {editMode && form.lineSuiteDetails.length > 1 && (
                        <IconButton
                          onClick={() => handleDeleteLineSuite(index)}
                          className={classes.deleteButton}
                          style={{ marginTop: 28, marginLeft: 8 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                </React.Fragment>
              ))}

              {/* Add New Line/Suite Button */}
              {editMode && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddLineSuite}
                    className={classes.addButton}
                  >
                    Add New Line/Suite
                  </Button>
                </Grid>
              )}
            </Grid>

            <Divider
              style={{
                margin: "80px 0px 0px 0px",
                backgroundColor: "#707070",
                height: 1,
              }}
            />
          </form>
        </div>
      </div>
    );
  }
);

export default ManufacturingPlantDetailsSection;
